import { handleActions } from 'redux-actions';
import qs from 'qs';

import cloneDeep from 'lodash/cloneDeep';
import _xor from 'lodash/xor';
import _uniq from 'lodash/uniq';
import _pick from 'lodash/pick';
import _keyBy from 'lodash/keyBy';
import _mapValues from 'lodash/mapValues';

import http from '../shared/http';
import intl from '../shared/intl';

import createStartTable from './components/table-start';
import createDropTable from './components/table-drop';

import createCalendar  from '../shared/calendar';
import { getDateRangeFromCalendar }  from '../shared/calendar';
import moment from 'moment';
import createFilters from '../shared/filters';
import endpoints from '../../config/endpoints';
import csvExport from '../shared/csvExport';

const startTable = createStartTable({ namespace: 'trafficAccounting', stateNodeName: 'startTable' });
const dropTable = createDropTable({ namespace: 'trafficAccounting', stateNodeName: 'dropTable' });

const calendar = createCalendar({
	namespace: 'trafficAccounting',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(4, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'trafficAccounting',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [{
		name: 'affiliate_ids',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}, {
		name: 'offer_ids',
		title: 'Offers',
		placeholder: 'Add offers',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.OFFERS
		}
	}, {
		name: 'country_ids',
		title: 'Countries',
		placeholder: 'Add country',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
		}
	}, {
		name: 'admin_emails',
		title: 'Admins',
		placeholder: 'Admins',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.ADMINS
		}
	}]
});

// actions types
const FETCH_DATA_SUCCESS = 'traffic-accounting/fetch-data-success';
const FETCH_DATA_ERROR = 'traffic-accounting/fetch-data-error';

const TOGGLE_PROGRESS_BAR = 'traffic-accounting/toggle-progress-bar';

const FETCH_DATA_FOR_EXPORT_SUCCESS = 'traffic-accounting/FETCH_DATA_FOR_EXPORT_SUCCESS';

const SET_ACTIVE_ROW = 'traffic-accounting/SET_ACTIVE_ROW';
const TOGGLE_UNCOVER_BY_ADMIN = 'traffic-accounting/TOGGLE_UNCOVER_BY_ADMIN';
const TOGGLE_UNCOVER_BY_AFFILIATE = 'traffic-accounting/TOGGLE_UNCOVER_BY_AFFILIATE';

const APPLY = 'traffic-accounting/apply';
const CLEAR = 'traffic-accounting/clear';
const CLEAR_UNCOVERED = 'traffic-accounting/CLEAR_UNCOVERED';

const SUBMIT_COMMENT_SUCCESS = 'traffic-accounting/SUBMIT_COMMENT_SUCCESS';
const DELETE_COMMENT_SUCCESS = 'traffic-accounting/DELETE_COMMENT_SUCCESS';
const GET_COMMENTS_SUCCESS = 'traffic-accounting/GET_COMMENTS_SUCCESS';

const GET_FIRST_CONVERSION_DATES = 'traffic-accounting/GET_FIRST_CONVERSION_DATES';

const CHANGE_TAB = 'traffic-accounting/CHANGE_TAB';

// permissions
export const allowedPermissions = {
	trafficAccountingView: 'traffic_accounting_view'
};

// initial state
const initialState = {
	data: null,
	progressBar: false,
	active_row_number: null,
	first_conversion_dates: [],
	uncovered_admins: [],
	uncovered_affiliates: [],
	calendar: calendar.initialState,
	startTable: startTable.initialState,
	dropTable: dropTable.initialState,
	filters: filters.initialState,
	currentTab: 'traffic-start',
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			const range = getDateRangeFromCalendar(selectors.getCalendar(getState()));
			const query = {
				date_range: range,
				uncovered_admins: selectors.getUncoveredAdmins(getState()),
				uncovered_affiliates: selectors.getUncoveredAffiliates(getState())
			};
			const { selectedFilters } = selectors.getFilters(getState());
			const tab = selectors.getCurrentTab(getState());

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					const names = filter.value.map(f => f.label);

					if (values.length) {
						query.filters = { ...query.filters, [filter.name]: values };
					}
					if (names.length && filter.name === 'admin_emails') {
						query.filters = { ...query.filters, [filter.name]: names };
					}
				});
			}

			dispatch({ type: TOGGLE_PROGRESS_BAR });

			return http({
				url: `v2/traffic-accounting/report/${tab}?${qs.stringify(query)}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch)
				.then(action => {
					if (action.type === FETCH_DATA_SUCCESS && tab === 'traffic-start' ) {
						dispatch(actions.getFirstConversionDates());
					}
				});
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		tableExport: (type) => (dispatch, getState) => {
			const range = getDateRangeFromCalendar(selectors.getCalendar(getState()));
			const query = {
				date_range: range,
				uncovered_admins: selectors.getUncoveredAdmins(getState()),
				uncovered_affiliates: selectors.getUncoveredAffiliates(getState()),
				expandAll: true
			};
			const { selectedFilters } = selectors.getFilters(getState());
			const tab = selectors.getCurrentTab(getState());

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					const names = filter.value.map(f => f.label);

					if (values.length) {
						query.filters = { ...query.filters, [filter.name]: values };
					}
					if (names.length && filter.name === 'admin_emails') {
						query.filters = { ...query.filters, [filter.name]: names };
					}
				});
			}
			return http({
				url: `v2/traffic-accounting/report/${tab}?${qs.stringify(query)}`,
				method: 'get',
				actions: [ FETCH_DATA_FOR_EXPORT_SUCCESS ]
			})(dispatch)
				.then(action => {
					if (action.type === FETCH_DATA_FOR_EXPORT_SUCCESS) {
						const dateCols = action.payload.data.columns;
						const defaultCols = [
							'admin', 'affiliate', 'affiliate_id', 'offer', 'offer_id', 'country', 'country_id'
						];
						let rows = action.payload.data.result;

						if (!rows || !rows[0])
							return;

						rows = rows.map(item => ({
							..._pick(item, defaultCols),
							..._mapValues(_keyBy(dateCols), val => ({
								total: item[`total_${val}`],
								approved: item[`approved_${val}`],
								payout_change: item[`payout_change_${val}`]
							}))
						}));

						const headers = Object.keys(rows[0]);

						const columns = rows.map(entity => headers.map(h => entity[h] || '-'));
						const rowsForExport = [].concat([ headers ], columns);

						csvExport(type, rowsForExport);
					}
				});
		},

		submitComment: (data) => (dispatch, getState) => {
			return http({
				url: `v2/traffic-accounting/comment`,
				method: 'PUT',
				actions: [ SUBMIT_COMMENT_SUCCESS ],
				data
			})(dispatch);
		},
		dropComment: (id) => (dispatch, getState) => {
			return http({
				url: `v2/traffic-accounting/comment`,
				method: 'DELETE',
				actions: [ DELETE_COMMENT_SUCCESS ],
				data: { id }
			})(dispatch);
		},

		getCommentsList: (searchParams) => (dispatch, getState) => {
			if (!searchParams.affiliate_id) {
				return Promise.resolve([]);
			}
			return http({
				url: `v2/traffic-accounting/comments?${qs.stringify(searchParams)}`,
				method: 'GET',
				actions: [ GET_COMMENTS_SUCCESS ]
			})(dispatch)
				.then(action => action.payload.data);
		},

		getFirstConversionDates: () => (dispatch, getState) => {
			const data = selectors.getData(getState()).result
				.filter(i => i.affiliate_id && i.offer_id && i.country_id);
			const range = getDateRangeFromCalendar(selectors.getCalendar(getState()));

			const query = {
				affiliate_ids: _uniq(data.map(i => i.affiliate_id)),
				offer_ids: _uniq(data.map(i => i.offer_id)),
				country_ids: _uniq(data.map(i => i.country_id)),
				range
			};

			return http({
				url: `v2/traffic-accounting/first-conversion-dates?${qs.stringify(query)}`,
				method: 'GET',
				actions: [ GET_FIRST_CONVERSION_DATES ]
			})(dispatch);
		},

		setActiveRow: (active_row_number) => ({
			type: SET_ACTIVE_ROW,
			payload: active_row_number
		}),

		toggleUncoverByAffiliate: (affiliate_id) => (dispatch, getState) => {
			dispatch({ type: TOGGLE_UNCOVER_BY_AFFILIATE, payload: affiliate_id });
			dispatch(actions.fetchData());
		},

		toggleUncoverByAdmin: (admin_id) => (dispatch, getState) => {
			dispatch({ type: TOGGLE_UNCOVER_BY_ADMIN, payload: admin_id });
			dispatch(actions.fetchData());
		},

		changeTab: (tab) => (dispatch, getState) => {
			if (tab !== selectors.getCurrentTab(getState())) {
				dispatch({ type: CLEAR });
				dispatch({ type: CHANGE_TAB, payload: tab });
				dispatch(actions.fetchData());
			}
		},

		apply: () => (dispatch) => {
			dispatch({ type: CLEAR_UNCOVERED });
			dispatch(actions.fetchData());
		},

		clear: () => ({ type: CLEAR })
	},
	startTable.actions,
	dropTable.actions,
	filters.actions,
	calendar.actions
);

// reducers
const reducers = Object.assign(
	{
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const data = action.payload.data;

			return {
				...state,
				data: data,
				progressBar: false,
				error: false
			};
		},
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, progressBar: !state.progressBar }),
		[CHANGE_TAB]: (state, action) => ({ ...state, currentTab: action.payload }),
		[GET_FIRST_CONVERSION_DATES]: (state, action) => ({ ...state, first_conversion_dates: action.payload.data }),
		[APPLY]: (state, action) => {
			return {
				...state,
				showProgressBar: false,
				data: null
			};
		},
		[TOGGLE_UNCOVER_BY_ADMIN]: (state, action) => ({
			...state, uncovered_admins: [..._xor(state.uncovered_admins, [ action.payload ])]
		}),
		[TOGGLE_UNCOVER_BY_AFFILIATE]: (state, action) => ({
			...state, uncovered_affiliates: [..._xor(state.uncovered_affiliates, [ action.payload ])]
		}),
		[FETCH_DATA_ERROR]: state => ({
			...state,
			progressBar: false,
			error: true
		}),
		[SET_ACTIVE_ROW]: (state, action) => ({
			...state,
			active_row_number: action.payload
		}),
		[CLEAR_UNCOVERED]: state => ({ ...state, uncovered_admins: [], uncovered_affiliates: [] }),
		[CLEAR]: state => ({ ...initialState })
	},
	startTable.reducers,
	dropTable.reducers,
	filters.reducers,
	calendar.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getCan: state => () => true,
	getData: state => state.trafficAccounting.data,
	getFirstConversionDates: state => state.trafficAccounting.first_conversion_dates,
	activeRowNumber: state => state.trafficAccounting.active_row_number,
	getFilters: state => filters.selectors.getFilters(
		state => state.trafficAccounting.filters,
		state => state
	)(state),
	getStartTable: startTable.selectors.getTable,
	getDropTable: dropTable.selectors.getTable,
	getIsToggledStartFrom10Approves: dropTable.selectors.getIsToggledStartFrom10Approves,
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.trafficAccounting.calendar,
		state => state
	)(state),
	getProgressBar: state => state.trafficAccounting.progressBar,
	getUncoveredAdmins: state => state.trafficAccounting.uncovered_admins,
	getUncoveredAffiliates: state => state.trafficAccounting.uncovered_affiliates,
	getCurrentTab: state => state.trafficAccounting.currentTab,
	getIntl: state => intl(state),
	getError: state => state.trafficAccounting.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/traffic-accounting/index.js